import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import RemoveModal from '../RemoveModal';
import * as St from './SinksOptions.styles';

import { SINK_OPTIONS, UserDesignOperation } from '~/core/enums';
import useAppliance from '~/core/hooks/useAppliance';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { ApplianceType } from '~/core/models';
import { useDrawingShapeContext } from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { setActiveDrawingObject } from '~/redux/slices/drawingShape';
import {
  resetAllSinkQuantities,
  resetSelectedBrowsingOption,
  resetSelectedSink,
  selectSelectedSinks,
  updateSelectedBrowsingOption,
} from '~/redux/slices/sink';
import { palette } from '~/theme';
import InformationDialog from '~molecules/InformationDialog';

type SinkOptionsProps = {
  selectedOption?: SINK_OPTIONS;
};

const SinkOptions = ({ selectedOption }: SinkOptionsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedSinks = useAppSelector(selectSelectedSinks);
  const { registerUserAction } = useRegisterUserAction();
  const { applianceTypesInDrawingArea, deleteAllAppliancesByType } =
    useAppliance();
  const {
    getters: { actualDrawingShapes },
  } = useDrawingShapeContext();

  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [isRemoveAllSinksModalVisible, setIsRemoveAllSinksModalVisible] =
    useState(false);

  const hasAnySelectedSinks =
    Object.values(selectedSinks).some((quantity) => quantity > 0) ||
    applianceTypesInDrawingArea([ApplianceType.SINK, ApplianceType.SINK_LABOR]);

  const selectSinkOption = (sinkOption: SINK_OPTIONS) => {
    if (sinkOption === SINK_OPTIONS.NO_SINKS && hasAnySelectedSinks) {
      setIsRemoveAllSinksModalVisible(true);

      return;
    }

    if (selectedOption === sinkOption && sinkOption !== SINK_OPTIONS.NO_SINKS) {
      dispatch(resetSelectedBrowsingOption());

      return;
    }

    dispatch(updateSelectedBrowsingOption(sinkOption));
  };

  const removeAllSinks = () => {
    registerUserAction(UserDesignOperation.REMOVE_ALL_SINKS);
    deleteAllAppliancesByType([ApplianceType.SINK, ApplianceType.SINK_LABOR]);
    dispatch(resetAllSinkQuantities());
    dispatch(resetSelectedSink());
    dispatch(updateSelectedBrowsingOption(SINK_OPTIONS.NO_SINKS));

    if (actualDrawingShapes.length > 0) {
      dispatch(
        setActiveDrawingObject({
          type: 'shape',
          id: actualDrawingShapes[0].id,
        })
      );
    }

    setIsRemoveAllSinksModalVisible(false);
  };

  return (
    <>
      <St.OptionsContainer>
        {Object.keys(SINK_OPTIONS).map((sinkOption, index) => (
          <St.OptionButton
            key={index}
            data-is-selected={sinkOption === selectedOption}
            onClick={() => selectSinkOption(sinkOption as SINK_OPTIONS)}
          >
            {t(`SCROLLABLE_AREA.SINK.OPTIONS.${sinkOption}`)}
          </St.OptionButton>
        ))}
      </St.OptionsContainer>
      <St.OptionTitle color={palette.dimGray}>
        {selectedOption === SINK_OPTIONS.SUPPLY_SINKS && (
          <>{t('SCROLLABLE_AREA.SINK.SUPPLIED_SINK_INFO')}</>
        )}
      </St.OptionTitle>
      <St.OptionTitle color={palette.dimGray}>
        {selectedOption === SINK_OPTIONS.BROWSE_SINKS && (
          <>
            {t('SCROLLABLE_AREA.SINK.BROWSE_SINK_INFO')}
            {selectedOption === SINK_OPTIONS.BROWSE_SINKS && (
              <St.InfoButton onClick={() => setOpenInfoModal(true)} />
            )}
          </>
        )}
      </St.OptionTitle>
      <InformationDialog
        title={t(`SCROLLABLE_AREA.SINK.MODAL_INFO_TITLE`)}
        visible={openInfoModal}
        content={
          selectedOption === SINK_OPTIONS.BROWSE_SINKS
            ? t(`SCROLLABLE_AREA.SINK.ADDITIONAL_VALUES_INFORMATION`)
            : t(
                `SCROLLABLE_AREA.SINK.ADDITIONAL_VALUES_INFORMATION_CUSTOMER_SUPPLIED`
              )
        }
        onClose={() => setOpenInfoModal(false)}
      />

      <RemoveModal
        title={t('SCROLLABLE_AREA.SINK.NO_SINK_IN_SPACE_TITLE')}
        description={t('SCROLLABLE_AREA.SINK.NO_SINK_IN_SPACE_DESCRIPTION')}
        buttons={{
          cancel: 'No, Keep',
          confirm: 'Yes, Delete',
        }}
        isVisible={isRemoveAllSinksModalVisible}
        handleClose={() => setIsRemoveAllSinksModalVisible(false)}
        handleChange={removeAllSinks}
      />
    </>
  );
};

export default SinkOptions;
